import React, { useEffect, useState, useMemo } from 'react'

import styled from 'styled-components'

interface IconProps {
	// Name of the icon file (without extension)
	name: string
	// Color to apply to the SVG
	color?: string
	// Optional size props
	width?: string
	height?: string
	// Additional class name
	className?: string
	// Optional onClick handler
	// @ts-ignore
	onClick?: (e?: React.MouseEvent<SVGSVGElement>) => void
	// Custom color for hover state
	hoverColor?: string
	// Disable the icon
	disabled?: boolean
	// Whether to stop propagation of click events (defaults to false)
	stopPropagation?: boolean
	// Custom cursor to use
	cursor?: string
}

// Global cache for SVG content to avoid redundant network requests
const svgCache = new Map<string, string>()
// Track loading states to prevent duplicate requests
const loadingStates = new Map<string, Promise<string>>()

// Helper function to load and cache SVG content
const loadSvgContent = async (name: string): Promise<string> => {
	// Return cached content if available
	if (svgCache.has(name)) {
		return svgCache.get(name)!
	}

	// Return existing promise if already loading
	if (loadingStates.has(name)) {
		return loadingStates.get(name)!
	}

	// Create new loading promise
	const loadingPromise = (async () => {
		try {
			// Dynamically import the SVG based on the name
			const module = await import(`@/assets/images/icons/${name}.svg`)

			let svgContent = ''

			if (typeof module.default === 'string') {
				// If it's a string URL, fetch the SVG content
				const response = await fetch(module.default)
				svgContent = await response.text()
			} else if (module.default) {
				// Some bundlers like SVGR convert SVGs to React components
				svgContent = `<svg viewBox="0 0 24 24">${module.default.toString()}</svg>`
			}

			// Cache the result
			svgCache.set(name, svgContent)
			// Remove from loading states
			loadingStates.delete(name)

			return svgContent
		} catch (error) {
			console.error(`Error loading SVG content for: ${name}`, error)
			// Remove from loading states on error
			loadingStates.delete(name)
			// Cache empty string to prevent repeated failed requests
			svgCache.set(name, '')
			return ''
		}
	})()

	// Store the loading promise
	loadingStates.set(name, loadingPromise)

	return loadingPromise
}

const Icon: React.FC<IconProps> = ({
	name,
	color,
	width = '24px',
	height = '24px',
	className = '',
	onClick,
	hoverColor,
	disabled = false,
	stopPropagation = false,
	cursor,
}) => {
	const [svgContent, setSvgContent] = useState<string>('')
	const [isLoading, setIsLoading] = useState(true)

	// Memoize colors to prevent unnecessary re-renders
	const iconColor = useMemo(() => color, [color])
	const iconHoverColor = useMemo(() => hoverColor, [hoverColor])

	useEffect(() => {
		// Check if already cached
		if (svgCache.has(name)) {
			setSvgContent(svgCache.get(name)!)
			setIsLoading(false)
			return
		}

		// Load SVG content
		setIsLoading(true)
		loadSvgContent(name)
			.then((content) => {
				setSvgContent(content)
				setIsLoading(false)
			})
			.catch((error) => {
				console.error(`Error loading icon: ${name}`, error)
				setIsLoading(false)
			})
	}, [name])

	const handleClick = (e: React.MouseEvent<SVGSVGElement>) => {
		if (disabled) return

		// Only stop propagation if explicitly requested
		if (stopPropagation) {
			e.stopPropagation()
		}

		// Call the onClick handler if provided
		if (onClick) {
			onClick(e)
		}
	}

	// Memoize the icon element to prevent unnecessary re-renders
	const iconElement = useMemo(() => {
		if (isLoading) {
			return (
				<IconPlaceholder width={width} height={height} className={className} />
			)
		}

		return (
			<StyledIcon
				className={`mybt-icon ${className}`}
				$color={iconColor}
				$hoverColor={iconHoverColor}
				$disabled={disabled}
				$cursor={cursor}
				style={{ width, height }}
				// @ts-ignore
				onClick={onClick ? handleClick : undefined}
				dangerouslySetInnerHTML={{ __html: svgContent }}
			/>
		)
	}, [
		isLoading,
		width,
		height,
		className,
		iconColor,
		iconHoverColor,
		disabled,
		svgContent,
		onClick,
		handleClick,
	])

	return iconElement
}

const IconPlaceholder = styled.div<{ width?: string; height?: string }>`
	width: ${(props) => props.width || '24px'};
	height: ${(props) => props.height || '24px'};
	background-color: transparent;
`

const StyledIcon = styled.div<{
	$color?: string
	$hoverColor?: string
	$disabled?: boolean
	$cursor?: string
}>`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: ${(props) =>
		!props.$disabled ? props.$cursor || 'pointer' : props.$cursor || 'default'};
	opacity: ${(props) => (props.$disabled ? 0.5 : 1)};

	& svg {
		width: 100%;
		height: 100%;
	}

	& svg path,
	& svg rect,
	& svg circle,
	& svg ellipse,
	& svg line,
	& svg polyline,
	& svg polygon {
		fill: ${(props) => props.$color || 'currentColor'};
		stroke: none;
		transition: fill 0.2s ease;
	}

	&:hover svg path,
	&:hover svg rect,
	&:hover svg circle,
	&:hover svg ellipse,
	&:hover svg line,
	&:hover svg polyline,
	&:hover svg polygon {
		fill: ${(props) => {
			if (props.$disabled) return props.$color || 'currentColor'
			if (props.$hoverColor) return props.$hoverColor
			return props.$color || 'currentColor'
		}};
		stroke: none;
	}
`

// Export utility functions for advanced use cases
export const preloadIcon = (name: string): Promise<string> => {
	return loadSvgContent(name)
}

export const preloadIcons = (names: string[]): Promise<string[]> => {
	return Promise.all(names.map(preloadIcon))
}

export const clearIconCache = (): void => {
	svgCache.clear()
	loadingStates.clear()
}

export const getIconCacheSize = (): number => {
	return svgCache.size
}

export default Icon
